import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import { StepLayout } from './-components/step-layout'

// One-sentence floor for TAM grounding. Below this the LLM has effectively
// nothing to work with beyond the domain.
const MIN_DESCRIPTION_LENGTH = 20

export const Route = createFileRoute('/onboarding/ideal-customer')({
  component: IdealCustomerStep,
})

function IdealCustomerStep() {
  useOnboardingStepViewed('ideal_customer')
  const navigate = useNavigate()
  const { company, qualificationArguments, qualificationLogic, updateState } = useOnboarding()
  const [description, setDescription] = useState(company.businessDescription ?? '')
  const [icp, setIcp] = useState(qualificationArguments)
  const [fitCriteria, setFitCriteria] = useState(qualificationLogic)

  const trimmedDescription = description.trim()
  const descriptionTooShort = description.length > 0 && trimmedDescription.length < MIN_DESCRIPTION_LENGTH

  const handleNext = () => {
    updateState({
      company: { ...company, businessDescription: trimmedDescription || undefined },
      qualificationArguments: icp.trim(),
      qualificationLogic: fitCriteria.trim(),
      currentStep: 3,
    })
    navigate({ to: '/onboarding/company-territory' })
  }

  const handleBack = () => {
    navigate({ to: '/onboarding/email' })
  }

  return (
    <StepLayout
      title="Tell us about your customers"
      subtitle="The more specific you are, the better your Market Intelligence Report — these answers ground the AI research instead of forcing it to guess from your domain."
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="Continue"
      nextDisabled={trimmedDescription.length < MIN_DESCRIPTION_LENGTH}
      maxWidth="md"
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="business-description">
            What does your company do, and who do you sell to?
          </Label>
          <Textarea
            id="business-description"
            value={description}
            rows={3}
            placeholder='e.g. "We sell precision CNC tooling to aerospace and defense manufacturers across the US."'
            onChange={e => setDescription(e.target.value)}
            aria-invalid={descriptionTooShort}
          />
          <p className="text-xs text-muted-foreground">
            A sentence or two is plenty. Mention your products and the kinds of companies that buy them.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ideal-customer">
            Who's your ideal customer?
            {' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id="ideal-customer"
            value={icp}
            rows={4}
            placeholder='e.g. "Mid-sized job shops doing high-mix low-volume aerospace work, 50-300 employees, with at least one 5-axis machining center."'
            onChange={e => setIcp(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Industries, company size, equipment they use, anything that signals fit.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="fit-criteria">
            What makes a good fit vs. a bad fit?
            {' '}
            <span className="text-muted-foreground font-normal">(optional)</span>
          </Label>
          <Textarea
            id="fit-criteria"
            value={fitCriteria}
            rows={4}
            placeholder='e.g. "Good: certified to AS9100, growing headcount in engineering. Bad: pure prototype shops, no production volume, or single-spindle manual shops."'
            onChange={e => setFitCriteria(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Helps us score and rank accounts — you can refine this later in the customer-qualification page.
          </p>
        </div>
      </div>
    </StepLayout>
  )
}
